import { DEFAULT_PAGE_SIZE } from "../../component/common/TablePaginator";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import { api } from "../../util/api";
import { notify } from "../../util/toast";

import { approvalApi } from "./api/approvalApi";
import CreateApprovalModal from "./create/CreateApprovalModal";
import ApprovalDetail      from "./ApprovalDetail";
import ApprovalAdminPanel  from "./ApprovalAdminPanel";
import ApprovalNormalPanel from "./ApprovalNormalPanel";
import FormAdminPanel      from "./admin/FormAdminPanel";
import DetailModal         from "../../component/common/DetailModal";

const BASE = process.env.REACT_APP_API_REAL_URL;

function extractList(res) {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.content)) return res.content;
    if (Array.isArray(res?.data)) return res.data;
    return [];
}

export default function ApprovalPage() {
    const [tab, setTab]               = useState(0);
    const [loading, setLoading]       = useState(true);
    const [selected, setSelected]     = useState(null);
    const [viewStack, setViewStack]   = useState([]);   // 결재로 이동 시 위에 겹쳐 여는 열람용 모달 스택
    const [createOpen, setCreateOpen] = useState(false);
    const [editingApproval, setEditingApproval] = useState(null);  // 상신 후 수정 대상
    /* 유형 필터 선택지 — 옛 ApprovalType 7개가 아니라 «양식» 목록이다.
       목록에 뜨는 값이 양식명인데 필터만 옛 유형이면 고른 것과 보이는 것이 어긋난다. */
    const [formOptions, setFormOptions] = useState([]);
    const [myInfo, setMyInfo]         = useState(null);
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [submitterFilter, setSubmitterFilter] = useState("");
    const [typeFilter, setTypeFilter]           = useState("ALL");

    const [inboxApprovals, setInboxApprovals]         = useState([]);
    const [completedApprovals, setCompletedApprovals] = useState([]);
    const [sentApprovals, setSentApprovals]           = useState([]);
    /* 열람 결재 — 공람으로 지정돼 보기만 하는 문서. 결재함과 성격이 달라 병합하지 않고 따로 둔다. */
    const [viewingApprovals, setViewingApprovals]     = useState([]);
    const [allApprovals, setAllApprovals]             = useState([]);
    const [viewingUnread, setViewingUnread]           = useState(0);
    /* 일반 탭도 서버 페이지네이션 — 예전엔 size=9999 로 통째로 받아 화면이 잘랐다.
       문서가 몇백 건만 돼도 매번 수백 KB를 내려받고 정렬까지 화면이 했다. */
    const [page, setPage]           = useState(1);
    const [pageSize, setPageSize]   = useState(DEFAULT_PAGE_SIZE);
    const [totalPages, setTotalPages]       = useState(1);
    const [totalElements, setTotalElements] = useState(0);

    const [isAdmin, setIsAdmin]           = useState(false);
    const [adminMode, setAdminMode]       = useState(false);
    /* 관리자 모드 안의 두 화면: "approvals" 전체 결재 현황 / "forms" 양식 관리 */
    const [adminView, setAdminView]       = useState("approvals");
    const [adminApprovals, setAdminApprovals] = useState([]);
    const [adminLoading, setAdminLoading] = useState(false);
    const [adminTypeFilter, setAdminTypeFilter]     = useState("ALL");
    const [adminStatusFilter, setAdminStatusFilter] = useState("ALL");
    const [adminSubmitterFilter, setAdminSubmitterFilter] = useState("");
    const [adminApproverFilter, setAdminApproverFilter] = useState("");

    // 관리자 탭 서버 페이지네이션 (관리자는 전체 회사 결재 → 많을 수 있으므로 유지)
    const [adminPage, setAdminPage]         = useState(1);
    const [adminPageSize, setAdminPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [adminTotalPages, setAdminTotalPages] = useState(1);
    const [adminTotalElements, setAdminTotalElements] = useState(0);

    /* ── 내 정보 + 권한 ── */
    useEffect(() => {
        const token = localStorage.getItem("auth_token");
        if (!token) return;
        Promise.all([
            api.get(`${BASE}/api/employees/me`),
            api.get(`${BASE}/api/employees/me/permissions`),
        ]).then(([meData, permData]) => {
            setMyInfo(meData);
            const perms = Array.isArray(permData) ? permData : [];
            setIsAdmin(perms.includes("GLOBAL_ADMIN") || perms.includes("GLOBAL_ADMIN"));
        }).catch(() => {
            api.get(`${BASE}/api/employees/me`).then((r) => setMyInfo(r)).catch(() => {});
        });
    }, []);

    /* ── 유형 필터 선택지: 양식 카탈로그 ── */
    useEffect(() => {
        approvalApi.formOptions()
            .then((res) => {
                const list = res?.data ?? res ?? [];
                setFormOptions((Array.isArray(list) ? list : [])
                    .map((f) => ({ value: f.code, label: f.active ? f.name : `${f.name} (중지)` })));
            })
            .catch(() => {});
    }, []);

    /* ── 탭 인덱스 → 해당 탭만 신규 조회 ── */
    /** 서버 페이지 응답 → 목록 + 페이지 정보. 받은 결재(inbox)만 배열이라 따로 센다. */
    const applyPage = (res, setter) => {
        const list = extractList(res);
        setter(list);
        setTotalPages(res?.totalPages ?? 1);
        setTotalElements(res?.totalElements ?? list.length);
    };

    const loadTab = async (tabIndex, p = 1, size = pageSize) => {
        setLoading(true);
        try {
            if (tabIndex === 1) {
                // 받은 결재는 «지금 내 차례» 라 건수가 적다 — 페이지 없이 전부 가져온다.
                const list = extractList(await approvalApi.inbox());
                setInboxApprovals(list);
                setTotalPages(1);
                setTotalElements(list.length);
            } else if (tabIndex === 2) {
                applyPage(await approvalApi.completed(p - 1, size), setCompletedApprovals);
            } else if (tabIndex === 3) {
                applyPage(await approvalApi.sent(p - 1, size), setSentApprovals);
            } else if (tabIndex === 4) {
                applyPage(await approvalApi.viewing(p - 1, size), setViewingApprovals);
                approvalApi.viewingUnread().then((n) => setViewingUnread(Number(n) || 0)).catch(() => {});
            } else {
                // 전체 보기(0) — 서버가 합쳐서 한 페이지로 준다.
                applyPage(await approvalApi.all(p - 1, size), setAllApprovals);
            }
        } catch {
            notify("결재 목록 불러오기 실패", "error");
        } finally {
            setLoading(false);
        }
    };

    /* ── 초기 로드 + 사이드바 딥링크(?view=, ?create=)는 아래 location 기반 effect 에서 처리 ── */

    /* 전체 보기는 서버가 합쳐 준다 — 화면에서 병합·정렬하지 않는다. */

    /* ── 탭별 현재 데이터 (0:전체 보기, 1:받은, 2:처리완료, 3:올린) ── */
    const approvals = tab === 1 ? inboxApprovals
        : tab === 2 ? completedApprovals
        : tab === 3 ? sentApprovals
        : tab === 4 ? viewingApprovals
        : allApprovals;

    /* ── 새로고침: 현재 탭만 재조회 ── */
    const refresh = () => loadTab(tab, page);

    /* ── 삭제 후 전체 일반 탭 갱신 (받은결재·처리완료·올린결재 모두 영향 가능) ── */
    const refreshAll = () => loadTab(tab, page);

    /* ── 관리자 목록 로드 ── */
    const loadAdmin = async (p = adminPage) => {
        setAdminLoading(true);
        try {
            const res = await approvalApi.adminAll(adminStatusFilter, adminTypeFilter, p - 1, adminPageSize, adminSubmitterFilter, adminApproverFilter);
            const data = extractList(res);
            setAdminApprovals(data);
            setAdminTotalPages(res?.totalPages ?? 1);
            setAdminTotalElements(res?.totalElements ?? data.length);
        } catch {
            notify("관리자 목록 불러오기 실패", "error");
        } finally {
            setAdminLoading(false);
        }
    };

    useEffect(() => {
        if (adminMode && isAdmin) { setAdminPage(1); loadAdmin(1); }
    }, [adminMode, adminTypeFilter, adminStatusFilter]); // eslint-disable-line

    useEffect(() => {
        if (adminMode && isAdmin) loadAdmin(adminPage);
    }, [adminPage, adminPageSize]); // eslint-disable-line

    // 기안자·결재자 텍스트 검색을 서버로 — 입력 디바운스 후 page 1 부터 재조회.
    // (검색이 서버로 내려가므로 filteredAdminApprovals 의 클라이언트 필터는 현재 페이지 즉시반영용 보조로만 남는다)
    useEffect(() => {
        if (!(adminMode && isAdmin)) return;
        const t = setTimeout(() => { setAdminPage(1); loadAdmin(1); }, 350);
        return () => clearTimeout(t);
    }, [adminSubmitterFilter, adminApproverFilter]); // eslint-disable-line

    const handleSelectApproval = async (approval) => {
        try {
            const data = await approvalApi.detail(approval.id);
            setSelected(data?.data ?? data);
        } catch {
            setSelected(approval);
        }
    };

    /* 결재로 이동 — 현재 모달을 유지한 채 위에 새 열람용 모달을 겹쳐 연다. */
    const pushApprovalView = async (id) => {
        try {
            const data = await approvalApi.detail(id);
            setViewStack((s) => [...s, data?.data ?? data]);
        } catch {
            notify("결재를 불러오지 못했습니다.", "warning");
        }
    };
    /* idx 위치 모달(과 그 위 모달들)을 닫는다. */
    const popApprovalView = (idx) => setViewStack((s) => s.slice(0, idx));
    /* 스택 안 결재 상세 새로고침 (승인/반려 등 처리 후). */
    const reloadApprovalView = async (idx) => {
        const cur = viewStack[idx];
        if (!cur) return;
        try {
            const data = await approvalApi.detail(cur.id);
            setViewStack((s) => s.map((x, i) => (i === idx ? (data?.data ?? data) : x)));
        } catch { /* 무시 */ }
    };

    /* 알람으로부터 진입: ?openApproval=<id> 가 있으면 해당 결재 상세를 자동으로 연다.
       location.search 가 바뀔 때마다 동작 → 페이지에 이미 머물러 있는 상태에서
       다른 알람을 또 클릭해도 정상 처리. 쿼리는 처리 직후 제거해 새로고침 시 재오픈 안 됨.
       (history.replace 직후 effect 가 재실행되지만 ?openApproval 이 없어 일찍 return) */
    const history  = useHistory();
    const location = useLocation();

    /* 초기 로드 + 사이드바 딥링크 동기화:
       ?view=received → 받은 결재(탭1), ?view=sent → 올린 결재(탭3), 그 외 → 전체(탭0).
       ?create=1 이면 결재 작성 모달을 열고 파라미터를 제거(새로고침·재실행 시 재오픈 방지). */
    useEffect(() => {
        const sp = new URLSearchParams(location.search);
        const v = sp.get("view");
        const target = v === "received" ? 1 : v === "sent" ? 3 : v === "viewing" ? 4 : 0;
        setTab(target);
        setPage(1);
        loadTab(target, 1);
        if (sp.get("create") === "1") {
            setCreateOpen(true);
            sp.delete("create");
            history.replace({ pathname: location.pathname, search: sp.toString() ? `?${sp}` : "" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    /* 사이드바에서 «결재»를 누르면 처음 상태로 되돌린다 — 열린 상세를 닫고 맨 위로.
       한참 내려본 뒤 메뉴를 다시 누를 때가 이 동작이 필요한 순간이다.

       location.key 가 아니라 state.resetView 를 보는 이유: 이 화면은 딥링크 파라미터를 지우려고
       스스로 history.replace 를 부른다. key 로 판단하면 그 replace 까지 «메뉴를 눌렀다» 로 오해해
       방금 연 상세가 곧바로 닫힌다 — 실제로 ?openApproval= 링크가 열리지 않았다. */
    useEffect(() => {
        if (!location.state?.resetView) return;
        setSelected(null);
        setViewStack([]);
        window.scrollTo({ top: 0 });
        document.querySelector("main")?.scrollTo({ top: 0 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state?.resetView]);

    useEffect(() => {
        const sp = new URLSearchParams(location.search);
        const id = sp.get("openApproval");
        if (!id) return;
        let cancelled = false;
        (async () => {
            try {
                const data = await approvalApi.detail(Number(id));
                if (!cancelled) setSelected(data?.data ?? data);
            } catch {
                if (!cancelled) notify("결재를 불러오지 못했습니다.", "warning");
            } finally {
                history.replace(location.pathname);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const handleRefresh = async () => {
        refresh();
        if (adminMode && isAdmin) loadAdmin(adminPage);
        // 열려 있는 상세 모달도 최신 상태로 재조회 — 승인/반려/보류 처리 후 모달이 stale 하게 남아
        // 버튼이 재노출되고 다시 누르면 "결재 차례 아님" 오류가 나던 문제 방지.
        if (selected) {
            try {
                const data = await approvalApi.detail(selected.id);
                setSelected(data?.data ?? data);
            } catch { /* 무시 */ }
        }
    };

    /* ── 필터(모든 탭 공통): 상태·결재유형·기안자 ── */
    const filtered = useMemo(() => {
        const sub = submitterFilter.trim().toLowerCase();
        return approvals.filter((a) => {
            if (statusFilter !== "ALL" && a.status !== statusFilter) return false;
            if (typeFilter !== "ALL" && a.templateCode !== typeFilter) return false;
            if (sub) {
                const text = `${a.submitterName || ""} ${a.submitterDept || ""} ${a.submitterPosition || ""}`.toLowerCase();
                if (!text.includes(sub)) return false;
            }
            return true;
        });
    }, [approvals, statusFilter, typeFilter, submitterFilter]);

    const filteredAdminApprovals = useMemo(() => {
        const submitter = adminSubmitterFilter.trim().toLowerCase();
        const approver  = adminApproverFilter.trim().toLowerCase();

        return adminApprovals.filter((a) => {
            const submitterText = `${a.submitterName || ""} ${a.submitterDept || ""} ${a.submitterPosition || ""}`.toLowerCase();
            const approverText  = (a.lines || [])
                .map((l) => `${l.approverName || ""} ${l.approverDept || ""} ${l.approverPosition || ""}`)
                .join(" ").toLowerCase();

            if (submitter && !submitterText.includes(submitter)) return false;
            if (approver  && !approverText.includes(approver))   return false;
            return true;
        });
    }, [adminApprovals, adminSubmitterFilter, adminApproverFilter]);

    return (
        <Box sx={{ height: "100%", bgcolor: "#fff", overflowY: "auto" }}>
            <Box sx={{ p: 2.5 }}>
                {adminMode && adminView === "forms" ? (
                    <FormAdminPanel onExit={() => setAdminView("approvals")} myInfo={myInfo} />
                ) : adminMode ? (
                    <ApprovalAdminPanel
                        onOpenForms={() => { setAdminView("forms"); setSelected(null); }}
                        adminApprovals={filteredAdminApprovals}
                        totalCount={adminTotalElements}
                        adminLoading={adminLoading}
                        adminTypeFilter={adminTypeFilter}
                        setAdminTypeFilter={setAdminTypeFilter}
                        adminStatusFilter={adminStatusFilter}
                        setAdminStatusFilter={setAdminStatusFilter}
                        adminSubmitterFilter={adminSubmitterFilter}
                        setAdminSubmitterFilter={setAdminSubmitterFilter}
                        adminApproverFilter={adminApproverFilter}
                        setAdminApproverFilter={setAdminApproverFilter}
                        onExit={() => { setAdminMode(false); setAdminView("approvals"); setSelected(null); }}
                        onRefresh={() => loadAdmin(adminPage)}
                        selected={selected}
                        onSelect={handleSelectApproval}
                        page={adminPage}
                        pageSize={adminPageSize}
                        totalPages={adminTotalPages}
                        totalElements={adminTotalElements}
                        onPageChange={setAdminPage}
                        onPageSizeChange={v => { setAdminPageSize(v); setAdminPage(1); }}
                    />
                ) : (
                    <ApprovalNormalPanel
                        tab={tab}
                        setTab={(v) => { setTab(v); setStatusFilter("ALL"); setSelected(null); setPage(1); loadTab(v, 1); }}
                        approvals={approvals}
                        loading={loading}
                        filtered={filtered}
                        statusFilter={statusFilter}
                        setStatusFilter={setStatusFilter}
                        submitterFilter={submitterFilter}
                        setSubmitterFilter={setSubmitterFilter}
                        typeFilter={typeFilter}
                        formOptions={formOptions}
                        setTypeFilter={setTypeFilter}
                        selected={selected}
                        onSelect={handleSelectApproval}
                        isAdmin={isAdmin}
                        onEnterAdmin={() => { setAdminMode(true); setSelected(null); }}
                        onOpenCreate={() => setCreateOpen(true)}
                        viewingUnread={viewingUnread}
                        page={page}
                        pageSize={pageSize}
                        totalPages={totalPages}
                        totalElements={totalElements}
                        onPageChange={(p) => { setPage(p); loadTab(tab, p); }}
                        onPageSizeChange={(v) => { setPageSize(v); setPage(1); loadTab(tab, 1, v); }}
                        myId={myInfo?.id}
                    />
                )}
            </Box>

            {/* 행 클릭 시 결재 상세 모달 (관리자/정상 공통) */}
            <DetailModal
                open={Boolean(selected)}
                onClose={() => { setSelected(null); setViewStack([]); }}
                title="결재 상세"
                maxWidth={1500}
            >
                {selected && (
                    <ApprovalDetail
                        key={selected.id}
                        approval={selected}
                        myId={myInfo?.id}
                        adminMode={adminMode}
                        onRefresh={handleRefresh}
                        onDelete={() => { setSelected(null); refreshAll(); }}
                        onEdit={(a) => { setSelected(null); setEditingApproval(a); setCreateOpen(true); }}
                        onOpenApproval={pushApprovalView}
                    />
                )}
            </DetailModal>

            {/* 결재로 이동 — 이전 모달 위에 겹쳐 여는 열람용 모달 스택 */}
            {viewStack.map((appr, idx) => (
                <DetailModal
                    key={`stack-${idx}-${appr.id}`}
                    open
                    onClose={() => popApprovalView(idx)}
                    title="결재 상세 (연결된 결재)"
                    maxWidth={1500}
                >
                    <ApprovalDetail
                        key={appr.id}
                        approval={appr}
                        myId={myInfo?.id}
                        adminMode={adminMode}
                        onRefresh={() => reloadApprovalView(idx)}
                        onDelete={() => popApprovalView(idx)}
                        onOpenApproval={pushApprovalView}
                    />
                </DetailModal>
            ))}

            <CreateApprovalModal
                open={createOpen}
                editing={editingApproval}
                onClose={() => { setCreateOpen(false); setEditingApproval(null); }}
                onCreated={() => {
                    setTab(3);
                    setStatusFilter("ALL");
                    setSelected(null);
                    loadTab(3);
                }}
                myInfo={myInfo}
            />
        </Box>
    );
}
